import React, { useEffect, useState } from "react";

import PrimaryButton from "../buttons/PrimaryButton";
import Icon1 from "../icons/Icon";

// Persisted "dismiss" so hiding the promo sticks across reloads, but only for a
// while — it reappears after HIDE_DAYS. We store the timestamp until which it
// stays hidden.
const DISMISS_KEY = "hexaflow_promo_hidden_until";
// Once dismissed, the promo reappears after 7 days.
const HIDE_MS = 7 * 24 * 60 * 60 * 1000;

// App-wide floating HexaFlow promo for the client-pro (workspace) view.
// Self-contained: owns the launcher, the dismiss cross and the "développement
// sur mesure" modal, so it can be mounted once at the app shell level instead of
// living on the home screen.
const HexaFlowPromo = ({ orgData }) => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      return until > Date.now();
    } catch (e) {
      return false;
    }
  });

  // Auto-show again once the dismiss window elapses, without needing a reload.
  useEffect(() => {
    if (!hidden) return;
    let until = 0;
    try {
      until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    } catch (e) {
      until = 0;
    }
    const ms = until - Date.now();
    if (ms <= 0) {
      setHidden(false);
      return;
    }
    const t = setTimeout(() => setHidden(false), ms);
    return () => clearTimeout(t);
  }, [hidden]);

  const hide = (e) => {
    e.stopPropagation();
    setHidden(true);
    try {
      const until = Date.now() + HIDE_MS;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch (e) {
      // ignore — storage disabled
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating launcher */}
      <div
        onClick={() => setOpen(true)}
        className="fixed bottom-10 right-10 z-50 cursor-pointer hover:scale-105 transition-all duration-300 bg-white p-6"
        style={{
          borderRadius: 40,
          boxShadow:
            "0 32px 64px -16px rgba(0,0,0,0.28), 0 12px 24px -8px rgba(0,0,0,0.12)",
        }}
      >
        <div
          onClick={hide}
          className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-3 shadow-md cursor-pointer hover:scale-110 transition-all"
        >
          <Icon1 icon_name="close" size={22} fill="black" />
        </div>
        <img
          src="https://ik.imagekit.io/hexaflow/Frame%201.png"
          alt="HexaFlow — développement sur mesure"
          className="h-auto block"
          style={{
            width: 377,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomLeftRadius: 32,
            borderBottomRightRadius: 32,
          }}
        />
      </div>

      {/* Modal */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="w-screen h-screen z-70 bg-shadow_25 flex flex-col p-20 items-center justify-center fixed top-0 left-0"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="h-fit overflow-auto scrollbar-hide rounded-2xl"
          >
            <div className="transition-all max-w-full w-fit duration-500 relative flex flex-col rounded-2xl bg-wshadow_50 border-white border backdrop-blur-md min-h-fit items-center justify-center shadow-lg shadow-black">
              <div className="flex flex-row absolute right-4 top-4 justify-end p-2">
                <div
                  onClick={() => setOpen(false)}
                  className="top-4 z-30 right-4 cursor-pointer"
                >
                  <Icon1 icon_name="close" size={20} fill="black"></Icon1>
                </div>
              </div>
              <div className="z-20 h-fit gap-1 flex flex-col p-8 text-h3 w-fit items-start justify-center">
                <div className="text-black text-hx mt-2">
                  Développement sur mesure pour
                </div>{" "}
                <div className="text-black text-hx ">{orgData?.name}</div>{" "}
                <div className="flex flex-col gap-2 mt-4 max-w-c500 w-fit">
                  Nous proposons une gamme complète de services de développement
                  de logiciels personnalisés pour répondre aux besoins uniques
                  de votre entreprise.
                </div>
                <div className="flex flex-col gap-2 mt-3 max-w-c500 w-fit">
                  Que vous ayez besoin d'un outil automatisé, d'une intégration
                  CRM, d'un tableau de bord personnalisé ou d'une application
                  mobile dédiée, notre équipe d'experts est là pour vous aider à
                  chaque étape du processus.
                </div>
                <div className="mt-6 flex flex-col gap-3 items-center">
                  <PrimaryButton
                    onClick={() => {
                      window.open("https://hexaflow.fr/contact", "_blank");
                    }}
                    big={true}
                    className="w-fit"
                  >
                    Planifiez un échange gratuit
                  </PrimaryButton>
                </div>
                <div className="flex flex-row gap-2 items-center mt-4">
                  <div className="flex flex-col gap-2 text-h3 w-fit">
                    William de HexaFlow
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HexaFlowPromo;
